/**
 * FilterBuilder — smart filter UI with AND/OR grouping.
 *
 * Data model:
 *   { groups: [...], groupOperator: 'AND' | 'OR' }
 *   Within each group, conditions are AND-connected.
 *   Groups are connected by the selectable groupOperator (AND or OR).
 *
 * Backwards-compatible: decodes old flat FilterCondition[] arrays
 * as a single AND group with groupOperator defaulting to 'OR'.
 */
import { useState, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, X, Filter, ChevronDown, ChevronRight, Trash2 } from 'lucide-react';
import { cn } from '@/lib/cn';
import { Select } from '@/components/ui/Select';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { collapse } from '@/design/animations';
import { useCountryOptions } from '@/hooks/useCountryOptions';
import {
  FILTER_FIELDS,
  FILTER_OPERATORS,
  FILTER_FIELD_META,
} from '@/sections/product/product.constants';

// ─── Types ──────────────────────────────────────────────────────────────────

interface FilterCondition {
  id: string;
  field: string;
  operator: string;
  value: string;
}

interface FilterGroup {
  id: string;
  conditions: FilterCondition[];
  /** Logical operator between conditions within this group — defaults to 'AND' */
  conditionOperator?: 'AND' | 'OR';
}

interface FilterRoot {
  groups: FilterGroup[];
  /** Logical operator between groups — defaults to 'OR' for backward compat */
  groupOperator?: 'AND' | 'OR';
}

type FieldOption = { value: string; label: string };
type FieldMeta = { type: 'text' | 'dropdown'; placeholder?: string; options?: FieldOption[] };

interface FilterBuilderProps {
  /** Base64-encoded filter JSON from URL */
  value: string;
  onChange: (encoded: string) => void;
  /** Custom filter fields (defaults to product FILTER_FIELDS) */
  fields?: readonly FieldOption[] | FieldOption[];
  /** Custom filter operators (defaults to product FILTER_OPERATORS) */
  operators?: readonly FieldOption[] | FieldOption[];
  /** Custom field metadata (defaults to product FILTER_FIELD_META) */
  fieldMeta?: Record<string, FieldMeta>;
}

// ─── Encoding / Decoding (backwards-compatible) ─────────────────────────────

function encodeFilterRoot(root: FilterRoot): string {
  // Strip empty groups & conditions without values, preserve conditionOperator
  const cleaned: FilterRoot = {
    groups: root.groups
      .map((g) => ({ ...g, conditions: g.conditions, conditionOperator: g.conditionOperator ?? 'AND' }))
      .filter((g) => g.conditions.length > 0),
    groupOperator: root.groupOperator ?? 'OR',
  };
  if (cleaned.groups.length === 0) return '';
  try {
    return btoa(JSON.stringify(cleaned));
  } catch {
    return '';
  }
}

function decodeFilterRoot(encoded: string): FilterRoot {
  if (!encoded) return { groups: [], groupOperator: 'OR' };
  try {
    const parsed = JSON.parse(atob(encoded));
    // New format: { groups: [...], groupOperator? }
    if (parsed && Array.isArray(parsed.groups)) {
      return { ...parsed, groupOperator: parsed.groupOperator ?? 'OR' } as FilterRoot;
    }
    // Old format: FilterCondition[] (flat array) → wrap in single AND group
    if (Array.isArray(parsed)) {
      return {
        groups: [
          {
            id: `g_legacy`,
            conditions: parsed as FilterCondition[],
          },
        ],
        groupOperator: 'OR',
      };
    }
    return { groups: [], groupOperator: 'OR' };
  } catch {
    return { groups: [], groupOperator: 'OR' };
  }
}

function countConditions(root: FilterRoot): number {
  return root.groups.reduce((sum, g) => sum + g.conditions.length, 0);
}

// ─── Component ──────────────────────────────────────────────────────────────

export function FilterBuilder({ value, onChange, fields, operators, fieldMeta }: FilterBuilderProps) {
  const [expanded, setExpanded] = useState(false);

  const resolvedFields = fields ?? FILTER_FIELDS;
  const resolvedOperators = operators ?? FILTER_OPERATORS;
  const baseMeta = fieldMeta ?? FILTER_FIELD_META;

  // Auto-enhance any "country" field with dropdown options from ref_country_codes
  const countryOptions = useCountryOptions();
  const resolvedMeta = useMemo<Record<string, FieldMeta>>(() => {
    if (countryOptions.length === 0) return baseMeta;
    const hasCountryField = resolvedFields.some((f) => f.value === 'country');
    if (!hasCountryField) return baseMeta;
    return {
      ...baseMeta,
      country: { type: 'dropdown', options: countryOptions },
    };
  }, [baseMeta, countryOptions, resolvedFields]);

  const root = decodeFilterRoot(value);
  const total = countConditions(root);

  const emit = useCallback(
    (next: FilterRoot) => onChange(encodeFilterRoot(next)),
    [onChange],
  );

  // ─── Group operations ───────────────────────────────────────────────
  const addGroup = () => {
    const gid = `g_${Date.now()}`;
    const cid = `c_${Date.now()}`;
    const newGroup: FilterGroup = {
      id: gid,
      conditions: [{ id: cid, field: resolvedFields[0].value, operator: '=', value: '' }],
    };
    emit({ groups: [...root.groups, newGroup], groupOperator: root.groupOperator ?? 'OR' });
    setExpanded(true);
  };

  const removeGroup = (gid: string) => {
    emit({ groups: root.groups.filter((g) => g.id !== gid), groupOperator: root.groupOperator });
  };

  const toggleGroupOperator = () => {
    emit({ ...root, groupOperator: root.groupOperator === 'AND' ? 'OR' : 'AND' });
  };

  const toggleConditionOperator = (gid: string) => {
    emit({
      ...root,
      groups: root.groups.map((g) =>
        g.id === gid
          ? { ...g, conditionOperator: (g.conditionOperator ?? 'AND') === 'AND' ? 'OR' : 'AND' }
          : g,
      ),
    });
  };

  // ─── Condition operations ───────────────────────────────────────────
  const addCondition = (gid: string) => {
    const cid = `c_${Date.now()}`;
    emit({
      ...root,
      groups: root.groups.map((g) =>
        g.id === gid
          ? { ...g, conditions: [...g.conditions, { id: cid, field: resolvedFields[0].value, operator: '=', value: '' }] }
          : g,
      ),
    });
  };

  const removeCondition = (gid: string, cid: string) => {
    const updated = root.groups
      .map((g) =>
        g.id === gid
          ? { ...g, conditions: g.conditions.filter((c) => c.id !== cid) }
          : g,
      )
      .filter((g) => g.conditions.length > 0);
    emit({ ...root, groups: updated });
  };

  const updateCondition = (gid: string, cid: string, patch: Partial<FilterCondition>) => {
    emit({
      ...root,
      groups: root.groups.map((g) =>
        g.id === gid
          ? {
              ...g,
              conditions: g.conditions.map((c) => {
                if (c.id !== cid) return c;
                // Clear value when field type changes
                if (patch.field) {
                  const oldMeta = resolvedMeta[c.field];
                  const newMeta = resolvedMeta[patch.field];
                  if (oldMeta?.type !== newMeta?.type) patch.value = '';
                }
                return { ...c, ...patch };
              }),
            }
          : g,
      ),
    });
  };

  // ─── Quick add (adds to first group, or creates one) ────────────────
  const quickAdd = () => {
    if (root.groups.length === 0) {
      addGroup();
    } else {
      addCondition(root.groups[0].id);
    }
    setExpanded(true);
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-1.5 text-xs font-medium text-slate-600 hover:text-slate-900 transition-colors"
        >
          {expanded ? <ChevronDown className="w-3 h-3 text-slate-400" /> : <ChevronRight className="w-3 h-3 text-slate-400" />}
          <Filter className="w-3.5 h-3.5" />
          Filters
          {total > 0 && (
            <span className="bg-brand-50 text-brand-600 px-1.5 py-0.5 rounded text-2xs font-semibold">
              {total}
            </span>
          )}
        </button>
        <Button variant="ghost" size="sm" icon={<Plus />} onClick={quickAdd}>
          Add Filter
        </Button>
      </div>

      {/* Groups */}
      <AnimatePresence>
        {expanded && root.groups.length > 0 && (
          <motion.div
            variants={collapse}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="mt-3 space-y-0"
          >
            {root.groups.map((group, gIdx) => (
              <div key={group.id}>
                {/* AND/OR toggle between groups */}
                {gIdx > 0 && (
                  <div className="flex items-center gap-3 py-2">
                    <div className="flex-1 h-px bg-slate-200" />
                    <button
                      type="button"
                      onClick={toggleGroupOperator}
                      className={cn(
                        'text-2xs font-bold px-2.5 py-0.5 rounded-full uppercase tracking-wider transition-colors',
                        root.groupOperator === 'AND'
                          ? 'text-sky-600 bg-sky-50 hover:bg-sky-100'
                          : 'text-amber-600 bg-amber-50 hover:bg-amber-100',
                      )}
                    >
                      {root.groupOperator === 'AND' ? 'and' : 'or'}
                    </button>
                    <div className="flex-1 h-px bg-slate-200" />
                  </div>
                )}

                {/* Group card */}
                <div className="relative overflow-visible rounded-lg border border-slate-200 bg-slate-50/50 p-3 space-y-1.5">
                  {/* Group remove (only if more than 1 group) */}
                  {root.groups.length > 1 && (
                    <button
                      onClick={() => removeGroup(group.id)}
                      className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-white border border-slate-200 shadow-sm flex items-center justify-center text-slate-400 hover:text-red-500 hover:border-red-200 hover:bg-red-50 transition-colors z-10"
                      title="Remove group"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  )}

                  {/* Conditions */}
                  {group.conditions.map((cond, cIdx) => {
                    const meta = resolvedMeta[cond.field];
                    return (
                      <motion.div
                        key={cond.id}
                        initial={{ opacity: 0, y: -6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -6 }}
                        transition={{ duration: 0.12 }}
                        className="flex items-center gap-1.5"
                      >
                        {/* Logic label */}
                        {cIdx === 0 ? (
                          <span className="w-12 text-center text-2xs font-semibold uppercase shrink-0 select-none text-slate-400">
                            Where
                          </span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => toggleConditionOperator(group.id)}
                            className={cn(
                              'w-12 text-center text-2xs font-bold uppercase shrink-0 select-none rounded-full py-0.5 transition-colors cursor-pointer',
                              (group.conditionOperator ?? 'AND') === 'AND'
                                ? 'text-sky-600 bg-sky-50 hover:bg-sky-100'
                                : 'text-amber-600 bg-amber-50 hover:bg-amber-100',
                            )}
                            title={`Toggle between AND / OR (currently ${group.conditionOperator ?? 'AND'})`}
                          >
                            {(group.conditionOperator ?? 'AND') === 'AND' ? 'And' : 'Or'}
                          </button>
                        )}

                        {/* Field */}
                        <div className="w-40 shrink-0">
                          <Select
                            options={[...resolvedFields]}
                            value={cond.field}
                            onChange={(e) => updateCondition(group.id, cond.id, { field: e.target.value })}
                          />
                        </div>

                        {/* Operator */}
                        <div className="w-32 shrink-0">
                          <Select
                            options={[...resolvedOperators]}
                            value={cond.operator}
                            onChange={(e) => updateCondition(group.id, cond.id, { operator: e.target.value })}
                          />
                        </div>

                        {/* Value */}
                        <div className="flex-1 min-w-0">
                          {meta?.type === 'dropdown' && meta.options ? (
                            <Select
                              options={meta.options}
                              value={cond.value}
                              placeholder="Select..."
                              onChange={(e) => updateCondition(group.id, cond.id, { value: e.target.value })}
                            />
                          ) : (
                            <Input
                              placeholder={meta?.placeholder || 'Value...'}
                              value={cond.value}
                              onChange={(e) => updateCondition(group.id, cond.id, { value: e.target.value })}
                            />
                          )}
                        </div>

                        {/* Remove condition */}
                        <button
                          onClick={() => removeCondition(group.id, cond.id)}
                          className="w-7 h-7 flex items-center justify-center rounded text-slate-300 hover:text-red-500 hover:bg-red-50 transition-colors shrink-0"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </motion.div>
                    );
                  })}

                  {/* Add condition to this group */}
                  <button
                    onClick={() => addCondition(group.id)}
                    className="flex items-center gap-1 ml-12 text-2xs font-medium text-slate-400 hover:text-brand-600 transition-colors pt-0.5"
                  >
                    <Plus className="w-3 h-3" />
                    {(group.conditionOperator ?? 'AND') === 'AND' ? 'And' : 'Or'} condition
                  </button>
                </div>
              </div>
            ))}

            {/* Add group */}
            <div className="pt-2">
              <button
                onClick={addGroup}
                className="flex items-center gap-1.5 text-2xs font-semibold text-slate-500 hover:text-slate-700 transition-colors"
              >
                <Plus className="w-3 h-3" />
                Add Group
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
